import type { PropertyDataType } from '~/types'

import { useFetchDocumentProperties } from './useFetchDocumentProperties'
import { queryKeys as documentQueryKeys, type QueryDocumentByIdCacheData } from '../useQueryDocument'

interface MetaOption { meta?: { dataType: PropertyDataType } }
type MetaValueItem = QueryDocumentByIdCacheData['data'][`${PropertyDataType}_values`][number]

function usePropertyMutation<
  TArgs extends { documentId: string, workspaceId: string },
  TResponse,
>(
  getFetchFn: (api: ReturnType<typeof useFetchDocumentProperties>) => (args: TArgs) => Promise<TResponse>,
  onCacheUpdate?: (ctx: {
    values: MetaValueItem[]
    variables: TArgs & MetaOption
    responseData: TResponse
  }) => MetaValueItem[],
) {
  return () => {
    const fetchFn = getFetchFn(useFetchDocumentProperties())
    const queryClient = useQueryClient()

    const getQueryKey = ({ documentId, workspaceId }: { documentId: string, workspaceId: string }) =>
      documentQueryKeys.byId({
        id: documentId,
        workspaceId,
      }).queryKey

    return useMutation({
      mutationFn: (variables: TArgs & MetaOption) => {
        const { meta: _, ...args } = variables
        return fetchFn(args as TArgs)
      },
      onMutate: (variables) => {
        queryClient.cancelQueries({ queryKey: getQueryKey(variables) })
      },
      onError: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: getQueryKey(variables) })
      },
      onSuccess: (responseData, variables) => {
        const dataType = variables.meta?.dataType
        if (!dataType || !onCacheUpdate) {
          queryClient.invalidateQueries({ queryKey: getQueryKey(variables) })
          return
        }

        const valueKey = getDocumentMetadataValueKey(dataType)

        queryClient.setQueriesData<QueryDocumentByIdCacheData>(
          { queryKey: getQueryKey(variables) },
          (oldData) => {
            if (!oldData)
              return oldData

            return {
              ...oldData,
              data: {
                ...oldData.data,
                [valueKey]: onCacheUpdate({
                  values: oldData.data[valueKey],
                  variables,
                  responseData,
                }),
              },
            }
          },
          { updatedAt: Date.now() },
        )
      },
    })
  }
}

const create = usePropertyMutation(
  api => api.create,
  ({ values, responseData }) => [...values, responseData.data],
)

const update = usePropertyMutation(
  api => api.update,
  ({ values, variables, responseData }) =>
    values.map(v => v.id === variables.valueId ? responseData.data : v),
)

const drop = usePropertyMutation(
  api => api.drop,
  ({ values, variables }) =>
    values.filter(v => v.id !== variables.valueId),
)

export const useMutationDocumentProperties = {
  create,
  update,
  drop,
}
